import { useState } from 'react';
import {
  Flex,
  Button,
  useComputedColorScheme,
  useMantineTheme,
  Modal,
  Text,
  Group,
} from '@mantine/core';
import { UseMutationResult } from '@tanstack/react-query';
import { CreateRowButton } from 'components/common';
import { DisableUserButton } from 'components/common/DisableUserButton';

import { User } from 'interfaces/user';
import { ResendPasswordResponse } from 'api/action';
import { MRT_TableInstance } from 'mantine-react-table';

// Icons
import {
  IconPasswordUser,
  IconSend2,
  IconAlertTriangleFilled,
} from '@tabler/icons-react';

// Utils
import { buttonTextColor } from 'utils/buttonUtils';

// Sentry
import * as Sentry from '@sentry/react';

interface UserToolBarProps {
  table: MRT_TableInstance<User>;
  isAdmin: boolean;
  canModify: boolean;
  disableQuery: UseMutationResult<User, Error>;
  resendPassword: UseMutationResult<ResendPasswordResponse>;
}

export const UserToolBar: React.FC<UserToolBarProps> = ({
  table,
  isAdmin,
  canModify,
  disableQuery,
  resendPassword,
}) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const theme = useMantineTheme();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const handleDisableUser = async (id: string) => {
    await disableQuery.mutateAsync(id);
    table.resetRowSelection();
  };

  const handleClickResendPassword = () => {
    setOpenModal(true);
  };

  const handleResendPassword = async (email: string) => {
    if (!email) return;
    setLoading(true);
    try {
      await resendPassword.mutateAsync(email);
      setOpenModal(false);
      table.resetRowSelection();
    } catch (e) {
      Sentry.setContext('data', { email });
      Sentry.captureException(e);
    } finally {
      setOpenModal(false);
      setLoading(false);
    }
  };

  const selectedRow = table.getSelectedRowModel().rows[0];

  const ModalContent = () => {
    return (
      <>
        <Group justify="center">
          <IconAlertTriangleFilled
            size={76}
            color={theme.colors[`warning-yellow-${computedColorScheme}`][0]}
          />
        </Group>
        <Group justify="center">
          <Text size="xl" fw={700}>
            Are you sure?
          </Text>
          <Text c="dimmed" ta="center">
            You're about to reset access for{' '}
            <Text
              span
              c={theme.colors[`primary-${computedColorScheme}`][0]}
              inherit
            >
              {selectedRow?.original?.email}
            </Text>
            . Users who have not completed setup will receive a new welcome
            email. Active users will receive a password reset email.
          </Text>
        </Group>
        <Group justify="center" mt="md">
          <Button
            onClick={closeModal}
            color={'grey'}
            c={buttonTextColor(computedColorScheme, false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleResendPassword(selectedRow?.original?.email);
            }}
            color={'green'}
            c={buttonTextColor(computedColorScheme, false)}
            loading={loading}
            leftSection={<IconSend2 size={16} />}
            data-testid="confirm-resend-password"
          >
            Send Reset
          </Button>
        </Group>
      </>
    );
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  return (
    <Flex justify="flex-start" w={'100%'} gap={'lg'}>
      <Modal
        title=""
        opened={openModal}
        onClose={closeModal}
        size="md"
        data-testid="submission-modal"
      >
        <ModalContent />
      </Modal>
      <CreateRowButton<User> table={table} type="user" disabled={!canModify} />
      {selectedRow && (
        <>
          <Button
            onClick={handleClickResendPassword}
            variant="outline"
            radius="md"
            data-testid={`resend-password-button`}
            c={buttonTextColor(computedColorScheme, false)}
            leftSection={<IconPasswordUser />}
            disabled={!canModify}
          >
            Reset Access
          </Button>
        </>
      )}
      {isAdmin && selectedRow && (
        <DisableUserButton
          id={selectedRow.original.id}
          handleDisableUser={handleDisableUser}
          row={table.getSelectedRowModel().rows[0]}
        />
      )}
    </Flex>
  );
};
